import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, RefreshCw, Search, Store, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Pagination } from './ui/pagination';

import { useBranch } from '../contexts/BranchContext';
import { storeApiService, Store as StoreDto } from '../services/storeApi';
import { itemCategoryApiService, ItemCategory } from '../services/itemCategoryApi';
import { storeInOutReportApiService, StoreInOutDetailDto } from '../services/storeInOutReportApi';
import { getPaginationConfig } from '../config/uiConfig';

function toIsoStartOfDay(dateStr: string): string {
  // Backend expects ISO_LOCAL_DATE_TIME without timezone
  return `${dateStr}T00:00:00`;
}

function toIsoEndOfDay(dateStr: string): string {
  return `${dateStr}T23:59:59`;
}

function getDefaultStartDate(): string {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const y = first.getFullYear();
  const m = String(first.getMonth() + 1).padStart(2, '0');
  const day = String(first.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDefaultEndDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type ColumnSpec = {
  /** snake_case header label shown in UI/CSV */
  header: string;
  /** candidate keys to read from API row */
  keys: string[];
};

const reportColumns: ColumnSpec[] = [
  { header: 'store_name', keys: ['storeName', 'store_name', 'store', 'store_name_display'] },
  { header: 'item_name', keys: ['itemName', 'item_name', 'rawItemName', 'raw_item_name', 'materialName', 'material_name'] },
  { header: 'quantity', keys: ['quantity', 'qty', 'movementQty', 'movement_qty'] },
  { header: 'available_quantity', keys: ['availableQuantity', 'available_quantity'] },
  { header: 'unit_of_measure', keys: ['unitOfMeasure', 'unit_of_measure', 'uom', 'uomName', 'uom_name'] },
  { header: 'movement_date', keys: ['movementDate', 'movement_date', 'transactionDate', 'transaction_date', 'createdAt', 'created_at'] },
  { header: 'movement_type', keys: ['movementType', 'movement_type', 'transactionType', 'transaction_type', 'type'] },
  { header: 'rawitem_category', keys: ['categoryName', 'category_name', 'rawItemCategory', 'raw_item_category', 'rawCategoryName', 'raw_category_name'] },
];

const headerLabelMap: Record<string, string> = {
  store_name: 'Store Name',
  item_name: 'Item Name',
  quantity: 'Quantity',
  available_quantity: 'Available Quantity',
  unit_of_measure: 'Unit Of Measure',
  movement_date: 'Movement Date',
  movement_type: 'Movement Type',
  rawitem_category: 'Raw Item Category',
};

function toHeaderLabel(header: string): string {
  return headerLabelMap[header] || header;
}

function resolveCellValue(row: StoreInOutDetailDto, spec: ColumnSpec): string {
  for (const k of spec.keys) {
    const v = (row as any)?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
  }
  return '';
}

const StoreInOutReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBranch } = useBranch();

  const paginationConfig = getPaginationConfig();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<StoreInOutDetailDto[]>([]);

  const [stores, setStores] = useState<StoreDto[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [loadingMasters, setLoadingMasters] = useState(false);

  const [storeId, setStoreId] = useState<string>(searchParams.get('storeId') || 'all');
  const [categoryName, setCategoryName] = useState<string>(searchParams.get('categoryName') || 'all');
  const [startDate, setStartDate] = useState<string>(searchParams.get('startDate') || getDefaultStartDate());
  const [endDate, setEndDate] = useState<string>(searchParams.get('endDate') || getDefaultEndDate());
  const [searchText, setSearchText] = useState<string>(searchParams.get('q') || '');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(paginationConfig.defaultItemsPerPage);

  const loadMasters = async () => {
    if (!selectedBranch) return;
    try {
      setLoadingMasters(true);
      const [storesData, categoriesData] = await Promise.all([
        storeApiService.getStoresByBranch(selectedBranch),
        itemCategoryApiService.getItemCategories(),
      ]);
      setStores(storesData || []);
      setCategories(categoriesData || []);
    } catch {
      setStores([]);
      setCategories([]);
    } finally {
      setLoadingMasters(false);
    }
  };

  const loadReport = async () => {
    if (!selectedBranch) {
      toast.error('Please select a branch to view the report');
      return;
    }
    try {
      setLoading(true);
      const params = {
        branchId: selectedBranch,
        storeId: storeId !== 'all' ? Number(storeId) : undefined,
        startDate: startDate ? toIsoStartOfDay(startDate) : undefined,
        endDate: endDate ? toIsoEndOfDay(endDate) : undefined,
        categoryName: categoryName !== 'all' ? categoryName : undefined,
      };
      const response = await storeInOutReportApiService.getStoreInOutDetails(params);
      if (response.success) {
        setRows(response.data || []);
      } else {
        setRows([]);
        toast.error(response.message || 'Failed to load Store IN/OUT report');
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      void loadMasters();
      void loadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  // Keep URL in sync (for shareable links)
  useEffect(() => {
    const next = new URLSearchParams();
    if (storeId && storeId !== 'all') next.set('storeId', storeId);
    if (categoryName && categoryName !== 'all') next.set('categoryName', categoryName);
    if (startDate) next.set('startDate', startDate);
    if (endDate) next.set('endDate', endDate);
    if (searchText.trim()) next.set('q', searchText.trim());
    setSearchParams(next, { replace: true });
    setCurrentPage(1);
  }, [storeId, categoryName, startDate, endDate, searchText, setSearchParams]);

  const headers = useMemo(() => reportColumns.map((c) => c.header), []);

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = reportColumns
        .map((c) => resolveCellValue(r, c))
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, searchText]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage, itemsPerPage]);

  const showingRange = useMemo(() => {
    if (filteredRows.length === 0) return { from: 0, to: 0 };
    const from = (currentPage - 1) * itemsPerPage + 1;
    const to = Math.min(filteredRows.length, currentPage * itemsPerPage);
    return { from, to };
  }, [filteredRows.length, currentPage, itemsPerPage]);

  const clearFilters = () => {
    setStoreId('all');
    setCategoryName('all');
    setSearchText('');
    setStartDate(getDefaultStartDate());
    setEndDate(getDefaultEndDate());
  };

  const exportCsv = () => {
    if (filteredRows.length === 0) {
      toast.error('No data to export');
      return;
    }
    try {
      const csvRows = [
        headers.map(toHeaderLabel).join(','),
        ...filteredRows.map((r) =>
          headers
            .map((k) => {
              const spec = reportColumns.find((c) => c.header === k)!;
              const s = resolveCellValue(r, spec);
              return `"${s.replace(/"/g, '""')}"`;
            })
            .join(',')
        ),
      ];
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'store-in-out-detail-report.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filteredRows.length} records to CSV`);
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap bg-white/60 backdrop-blur-sm border border-primary-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
              <Store className="h-6 w-6" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">Store IN/OUT Detail Report</div>
              <div className="text-sm text-gray-600">Store movements filtered by store, date range, and category</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/reports')}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Reports
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadReport}
              disabled={loading}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={filteredRows.length === 0}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm border border-primary-200 rounded-lg shadow-sm p-6">
          <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="text-lg font-semibold text-primary-800">Filters</div>
              <div className="text-sm text-gray-600">Branch is taken from the branch selector in the header</div>
            </div>
          </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Store</Label>
                <Select value={storeId} onValueChange={setStoreId} disabled={loadingMasters}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="All stores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All stores</SelectItem>
                    {stores.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.storeName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Category</Label>
                <Select value={categoryName} onValueChange={setCategoryName} disabled={loadingMasters}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.categoryName}>
                        {c.categoryName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Start date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">End date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Search</Label>
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="pl-10 pr-10"
                    placeholder="Search in rows..."
                  />
                  {searchText ? (
                    <button
                      type="button"
                      onClick={() => setSearchText('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-4 flex-wrap gap-3">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{filteredRows.length}</span> row{filteredRows.length !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={clearFilters} className="border-gray-300 text-gray-700 hover:bg-gray-50">
                  Clear
                </Button>
                <Button size="sm" onClick={loadReport} disabled={loading} className="bg-primary-600 hover:bg-primary-700 text-white">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Apply
                </Button>
              </div>
            </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm border border-primary-200 rounded-lg shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                <span className="ml-2">Loading report...</span>
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="text-center py-10 text-gray-600">No data found for the selected filters.</div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-gray-600">
                    Showing <span className="font-semibold">{showingRange.from}-{showingRange.to}</span> of{' '}
                    <span className="font-semibold">{filteredRows.length}</span> records{' '}
                    <span className="text-gray-500">(filtered from {rows.length} total)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Items per page:</span>
                    <Select
                      value={String(itemsPerPage)}
                      onValueChange={(v) => {
                        setItemsPerPage(Number(v));
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[90px] h-8 border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paginationConfig.itemsPerPageOptions.map((n: number) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden bg-white">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          {headers.map((c) => (
                            <TableHead key={c} className="font-semibold whitespace-nowrap">
                              {toHeaderLabel(c)}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedRows.map((r, idx) => (
                          <TableRow key={(r as any).id ?? idx} className="hover:bg-gray-50">
                            {reportColumns.map((c) => (
                              <TableCell
                                key={c.header}
                                className={c.header === 'item_name' ? 'whitespace-normal' : 'whitespace-nowrap'}
                              >
                                {resolveCellValue(r, c) || '—'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="p-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="text-sm text-gray-600">
                    Page <span className="font-semibold">{currentPage}</span> of{' '}
                    <span className="font-semibold">{totalPages}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(p) => setCurrentPage(p)}
                    />
                  </div>
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
};

export default StoreInOutReportPage;

